/**
 * スキルサジェスト API 通信 + sessionStorage キャッシュ hook
 */
import { useEffect, useRef, useState } from "react";
import { restClient } from "../lib/rest-client";

// =====================================
// Types
// =====================================

type SkillSuggestItem = {
  name: string;
  category: string | null;
  iconUrl: string | null;
};

type SkillSuggestResponse = {
  skills: SkillSuggestItem[];
};

type UseSkillSuggestionsResult = {
  /** フィルタリング済みの候補リスト */
  suggestions: SkillSuggestItem[];
  /** ローディング状態 */
  loading: boolean;
  /** エラー */
  error: Error | undefined;
};

// =====================================
// Cache
// =====================================

const CACHE_KEY = "skill_suggestions_cache";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

type CacheEntry = {
  skills: SkillSuggestItem[];
  timestamp: number;
};

/**
 * sessionStorage からキャッシュを取得
 * @return {SkillSuggestItem[] | null} キャッシュされたスキルリスト、期限切れの場合はnull
 */
function getCachedSkills(): SkillSuggestItem[] | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const entry: CacheEntry = JSON.parse(raw);
    if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return entry.skills;
  } catch {
    return null;
  }
}

/**
 * sessionStorage にキャッシュを保存
 * @param {SkillSuggestItem[]} skills スキルリスト
 */
function setCachedSkills(skills: SkillSuggestItem[]): void {
  try {
    const entry: CacheEntry = { skills, timestamp: Date.now() };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // sessionStorage full or unavailable — ignore
  }
}

// =====================================
// Hook
// =====================================

/**
 * スキルサジェスト候補を取得する hook
 *
 * 初回呼び出し時に API から全スキルリストを取得し sessionStorage にキャッシュ。
 * 2回目以降はキャッシュからクライアント側でフィルタリング（TTL: 5分）。
 *
 * @param {string} query 検索文字列（case-insensitive 前方一致）
 * @return {UseSkillSuggestionsResult} サジェスト候補、ローディング状態、エラー
 */
export function useSkillSuggestions(query: string): UseSkillSuggestionsResult {
  const [allSkills, setAllSkills] = useState<SkillSuggestItem[]>(() => {
    return getCachedSkills() || [];
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const fetchedRef = useRef(false);

  // Fetch all skills on mount (if not cached)
  useEffect(() => {
    if (fetchedRef.current) return;
    const cached = getCachedSkills();
    if (cached) {
      setAllSkills(cached);
      fetchedRef.current = true;
      return;
    }

    fetchedRef.current = true;
    setLoading(true);
    restClient
      .get<SkillSuggestResponse>("/skills/suggest", { limit: "500" })
      .then(data => {
        setAllSkills(data.skills);
        setCachedSkills(data.skills);
      })
      .catch(e => {
        setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Client-side filtering
  const suggestions = filterSkills(allSkills, query);

  return { suggestions, loading, error };
}

/**
 * スキルリストを case-insensitive 前方一致でフィルタリング
 * @param {SkillSuggestItem[]} skills 全スキルリスト
 * @param {string} query 検索文字列
 * @return {SkillSuggestItem[]} フィルタリング済みリスト
 */
function filterSkills(
  skills: SkillSuggestItem[],
  query: string,
): SkillSuggestItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return skills;
  return skills.filter(s => s.name.toLowerCase().startsWith(q));
}

/**
 * スキルサジェストキャッシュをクリアする
 */
export function clearSkillSuggestionsCache(): void {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}
